import { Home, Plane, UtensilsCrossed, Clock, Settings } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import { cn } from "@/lib/utils";

interface MobileNavProps {
  activeTab?: "home" | "flights" | "dining" | "wait-times";
  onTabChange?: (tab: "flights" | "dining") => void;
}

const MobileNav = ({ activeTab = "home", onTabChange }: MobileNavProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  
  const isOnDashboard = location.pathname === "/dashboard";

  const navItems = [
    { 
      id: "home" as const, 
      icon: Home, 
      label: "Home",
      action: () => navigate("/home")
    },
    { 
      id: "wait-times" as const, 
      icon: Clock, 
      label: "Wait Times",
      action: () => {
        if (!isOnDashboard) navigate("/dashboard");
      }
    },
    { 
      id: "flights" as const, 
      icon: Plane, 
      label: "Flights",
      action: () => {
        if (isOnDashboard && onTabChange) {
          onTabChange("flights");
        } else {
          navigate("/dashboard", { state: { defaultTab: "flights" } });
        }
      }
    },
    { 
      id: "dining" as const, 
      icon: UtensilsCrossed, 
      label: "Dining",
      action: () => {
        if (isOnDashboard && onTabChange) {
          onTabChange("dining");
        } else {
          navigate("/dashboard", { state: { defaultTab: "dining" } });
        }
      }
    },
  ];

  return (
    <nav 
      className="fixed bottom-0 left-0 right-0 z-50 bg-background/95 backdrop-blur-lg border-t border-border md:hidden safe-area-bottom shadow-lg"
      role="navigation"
      aria-label="Main navigation"
    >
      <div className="flex items-center justify-around px-2 py-1">
        {navItems.map((item) => {
          const Icon = item.icon;
          const isActive = activeTab === item.id;
          
          return (
            <button
              key={item.id}
              onClick={item.action}
              className={cn(
                "flex flex-col items-center justify-center min-w-[64px] min-h-[56px] px-3 py-2 rounded-xl transition-all duration-200",
                "active:scale-95 touch-manipulation focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
                isActive 
                  ? "text-primary bg-primary/10" 
                  : "text-muted-foreground hover:text-foreground hover:bg-muted/50"
              )}
              aria-current={isActive ? "page" : undefined}
              aria-label={item.label}
            >
              <Icon 
                className={cn(
                  "h-5 w-5 mb-1 transition-transform",
                  isActive && "scale-110"
                )} 
                aria-hidden="true"
              />
              <span className={cn(
                "text-xs font-medium",
                isActive && "font-semibold"
              )}>
                {item.label}
              </span>
            </button>
          );
        })}
      </div>
    </nav>
  );
};

export default MobileNav;
